import type { ThreadEscalationInfo } from "@redotech/merchant-sdk/rpc/schema/agents/merchant-support-agent/message-merchant-support-agent";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { GlobalAgentContext } from "@redotech/redo-merchant-app-common/global-agent-context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import type {
  AssistantMessageContent,
  Message,
  ToolResultPart,
  UserMessageContent,
} from "@redotech/redo-model/agents/llm-messages";
import { AiSlideOutChatGlobal } from "@redotech/redo-web/ai/chat/components/slide-out-chat-modal-global";
import { AiChatContext } from "@redotech/redo-web/ai/chat/contexts/ai-chat-context";
import { useAiSlidePanel } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import {
  MessageAuthorContext,
  MessageAuthorContextType,
  MessageAuthorInfo,
} from "@redotech/redo-web/ai/chat/contexts/message-author-context";
import { ObjectId } from "bson";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useEscalationContext } from "./escalation-context";
import { EscalationCsatForm } from "./escalation-csat-form";
import { EscalationStatus } from "./escalation-status";
import { useCodeSearchNotification } from "./use-code-search-notification";
import {
  clearEscalationNotification,
  useEscalationNotifications,
} from "./use-escalation-notifications";
import {
  sanitizeIntercomBody,
  useHumanMessageHandler,
} from "./use-human-message-handler";

interface SupportThread {
  _id: string;
  updatedAt: string | Date;
  messages: { id: string; role: string; content: string | unknown[] }[];
  escalation?: ThreadEscalationInfo;
}

export interface HelpChatGlobalProps {
  onChatOpen?: () => void;
  onLoadThread?: (threadId: string, thread: SupportThread) => void;
  onRegisterTriageThreadCallback?: (
    callback: (threadId: string) => void,
  ) => void | (() => void);
}

export const HelpChatGlobal = memo(function HelpChatGlobal({
  onChatOpen,
  onLoadThread,
  onRegisterTriageThreadCallback,
}: HelpChatGlobalProps) {
  const [currentThreadId, setCurrentThreadId] = useState<string | undefined>();
  const [threadsRefreshKey, setThreadsRefreshKey] = useState(0);
  const hasInitializedRef = useRef(false);
  const processedMessageIdsRef = useRef<Set<string>>(new Set());
  const authorRegistryRef = useRef<Map<string, MessageAuthorInfo>>(new Map());
  const pendingThreadLoadInFlightRef = useRef(false);
  const isOpenRef = useRef(false);

  const messageAuthorContextValue = useMemo<MessageAuthorContextType>(
    () => ({
      getAuthor: (messageId: string) =>
        authorRegistryRef.current.get(messageId),
    }),
    [],
  );
  const user = useRequiredContext(UserContext);
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const { close, open, isOpen, panelOptions, clearPanelOptions } =
    useAiSlidePanel();
  const { pageContext, defaultSuggestedQuestions } =
    useContext(GlobalAgentContext);

  // Keep isOpenRef in sync
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const escalationContext = useEscalationContext();
  const { clearEscalation } = escalationContext;
  const { setEscalation, setTriageThreadId, setNavigateToThread } =
    escalationContext;

  const hasActiveEscalation = escalationContext.escalation.status !== "idle";
  const suggestedQuestions = hasActiveEscalation
    ? undefined
    : (pageContext?.suggestedQuestions ?? defaultSuggestedQuestions);

  const refreshThreads = useCallback(() => {
    setThreadsRefreshKey((prev) => prev + 1);
  }, []);

  const handleNewTriageThread = useCallback(
    (threadId: string) => {
      setCurrentThreadId(threadId);
      refreshThreads();
    },
    [refreshThreads],
  );

  useEffect(() => {
    if (onRegisterTriageThreadCallback) {
      const cleanup = onRegisterTriageThreadCallback(handleNewTriageThread);
      if (typeof cleanup === "function") {
        return cleanup;
      }
      return undefined;
    }
    return undefined;
  }, [onRegisterTriageThreadCallback, handleNewTriageThread]);

  // Refresh threads when a new escalation is created so the thread
  // objects have up-to-date escalation info for navigation.
  useEffect(() => {
    if (
      escalationContext.escalation.status === "waiting" &&
      escalationContext.escalation.escalationId
    ) {
      refreshThreads();
    }
  }, [
    escalationContext.escalation.status,
    escalationContext.escalation.escalationId,
    refreshThreads,
  ]);

  const threadsLoad = useLoad(async () => {
    if (!user.email) {
      return { threads: [] };
    }
    return await merchantRpcClient.getMerchantSupportAgentThreads({
      userEmail: user.email,
    });
  }, [merchantRpcClient, user.email, threadsRefreshKey]);

  const {
    clearMessages,
    clearAssets,
    setInputValue,
    stopStream,
    addMessage,
    sendMessage,
  } = useContext(AiChatContext);

  const threads = useMemo(
    () => threadsLoad.value?.threads || [],
    [threadsLoad.value?.threads],
  );

  const loadThreadIntoChat = useHandler(
    async (threadId: string, thread: SupportThread) => {
      // Check if current escalation context matches this thread
      const contextEscalation =
        escalationContext.triageThreadId === threadId &&
        escalationContext.escalation.escalationId &&
        (escalationContext.escalation.status === "waiting" ||
          escalationContext.escalation.status === "human-active")
          ? {
              escalationId: escalationContext.escalation.escalationId,
              status: escalationContext.escalation.status as
                | "waiting"
                | "human-active",
              noticeTopic: escalationContext.escalation.noticeTopic || "",
              humanJoinedAt: null,
              closedAt: null,
            }
          : undefined;
      const threadEscalation = thread.escalation ?? contextEscalation;

      if (threadEscalation) {
        setIsLoadingThread(true);
      }

      try {
        stopStream();
        clearAssets();
        clearMessages();
        setInputValue("");
        clearEscalation();
        setAgentName(null);
        setAgentAvatarUrl(null);
        processedMessageIdsRef.current.clear();
        authorRegistryRef.current.clear();

        // Add messages from the thread
        for (const [index, message] of thread.messages.entries()) {
          const messageId = message.id ?? new ObjectId().toString() + index;

          if (message.role === "tool") {
            const toolMessage: Message = {
              id: messageId,
              role: "tool",
              content: message.content as ToolResultPart[],
            };
            addMessage(toolMessage);
            continue;
          }

          const rawContent = message.content;
          const contentToUse =
            typeof rawContent === "string" || Array.isArray(rawContent)
              ? rawContent
              : String(rawContent ?? "");

          if (typeof contentToUse === "string" && contentToUse.trim() === "") {
            continue;
          }

          if (message.role === "user") {
            const userMessage: Message = {
              id: messageId,
              role: "user",
              content: contentToUse as string | UserMessageContent[],
            };
            addMessage(userMessage);
          } else if (message.role === "assistant") {
            const assistantMessage: Message = {
              id: messageId,
              role: "assistant",
              content: contentToUse as string | AssistantMessageContent[],
            };
            addMessage(assistantMessage);
          }
        }

        // Fetch and display escalation messages if present
        if (threadEscalation) {
          const escalation = threadEscalation;
          const isActiveEscalation =
            escalation.status === "waiting" ||
            escalation.status === "human-active";

          let freshEscalationStatus = escalation.status;
          try {
            const messagesResult =
              await merchantRpcClient.getEscalationMessages({
                escalationId: escalation.escalationId,
              });
            freshEscalationStatus = messagesResult.status;

            // Combine and sort all messages by createdAt
            type CombinedMessage =
              | {
                  type: "human";
                  message: (typeof messagesResult.messages)[number];
                }
              | {
                  type: "user";
                  message: (typeof messagesResult.userMessages)[number];
                };

            const allMessages: CombinedMessage[] = [
              ...messagesResult.messages.map((m) => ({
                type: "human" as const,
                message: m,
              })),
              ...messagesResult.userMessages.map((m) => ({
                type: "user" as const,
                message: m,
              })),
            ].sort((a, b) => a.message.createdAt - b.message.createdAt);

            for (const { type, message } of allMessages) {
              processedMessageIdsRef.current.add(message.id);

              if (type === "human") {
                const humanMessage =
                  message as (typeof messagesResult.messages)[number];

                if (humanMessage.authorName) {
                  setAgentName(humanMessage.authorName.split(" ")[0] ?? null);
                }
                if (humanMessage.authorAvatarUrl) {
                  setAgentAvatarUrl(humanMessage.authorAvatarUrl);
                }

                let content: string | AssistantMessageContent[] =
                  sanitizeIntercomBody(humanMessage.body);

                if (
                  humanMessage.attachments &&
                  humanMessage.attachments.length > 0
                ) {
                  const contentParts: AssistantMessageContent[] = [];
                  const cleanedBody = sanitizeIntercomBody(humanMessage.body);
                  if (cleanedBody) {
                    contentParts.push({
                      type: "text" as const,
                      text: cleanedBody,
                    });
                  }

                  for (const attachment of humanMessage.attachments) {
                    const isImage =
                      attachment.contentType?.startsWith("image/");
                    const isVideo =
                      attachment.contentType?.startsWith("video/");
                    const attachmentData = JSON.stringify({
                      url: attachment.url,
                      type: attachment.contentType,
                      name:
                        isImage || isVideo
                          ? attachment.name
                          : attachment.name || "Attachment",
                    });
                    const marker = isImage
                      ? "image-data"
                      : isVideo
                        ? "video-data"
                        : "document-data";
                    contentParts.push({
                      type: "text" as const,
                      text: `[[[[${marker}:${attachmentData}]]]]`,
                    });
                  }
                  content = contentParts;
                }

                const messageId = `human-${humanMessage.id}`;
                authorRegistryRef.current.set(messageId, {
                  authorName: humanMessage.authorName?.split(" ")[0] ?? null,
                  authorAvatarUrl: humanMessage.authorAvatarUrl ?? null,
                });

                addMessage({ id: messageId, role: "assistant", content });
              } else {
                const userMessage =
                  message as (typeof messagesResult.userMessages)[number];

                let userContent: string | UserMessageContent[] =
                  sanitizeIntercomBody(userMessage.body);

                if (
                  userMessage.attachments &&
                  userMessage.attachments.length > 0
                ) {
                  const contentParts: UserMessageContent[] = [];
                  const cleanedBody = sanitizeIntercomBody(userMessage.body);
                  if (cleanedBody) {
                    contentParts.push({
                      type: "text" as const,
                      text: cleanedBody,
                    });
                  }

                  for (const attachment of userMessage.attachments) {
                    const isImage =
                      attachment.contentType?.startsWith("image/");
                    const isVideo =
                      attachment.contentType?.startsWith("video/");

                    if (isImage) {
                      // Use image content part — UserMessageComponent renders these natively
                      contentParts.push({
                        type: "image" as const,
                        image: attachment.url,
                      });
                    } else {
                      // Videos and documents use text markers — UserMessageComponent handles these

                      const attachmentData = JSON.stringify({
                        url: attachment.url,
                        type: attachment.contentType,
                        name: isVideo
                          ? attachment.name
                          : attachment.name || "Attachment",
                      });
                      const marker = isVideo ? "video-data" : "document-data";
                      contentParts.push({
                        type: "text" as const,
                        text: `[[[[${marker}:${attachmentData}]]]]`,
                      });
                    }
                  }
                  userContent = contentParts;
                }

                addMessage({
                  id: `escalation-user-${userMessage.id}`,
                  role: "user",
                  content: userContent,
                });
              }
            }
          } catch {
            // Escalation messages failed to load — thread still displays
          }

          // Set up escalation context using the fresh status from
          // getEscalationMessages instead of the stale thread snapshot.
          if (isActiveEscalation) {
            setTriageThreadId(threadId);
            setEscalation({
              escalationId: escalation.escalationId,
              noticeTopic: escalation.noticeTopic,
              status: freshEscalationStatus,
              intercomConversationId: null,
            });
          } else if (freshEscalationStatus === "closed") {
            // Set context for closed escalations so the CSAT form can render
            setTriageThreadId(threadId);
            setEscalation({
              escalationId: escalation.escalationId,
              noticeTopic: escalation.noticeTopic,
              status: "closed",
              intercomConversationId: null,
            });
          }
        }

        onLoadThread?.(threadId, thread);
        setCurrentThreadId(threadId);
      } catch {
        // Thread load failed — UI resets via finally block
      } finally {
        setIsLoadingThread(false);
      }
    },
  );

  const navigateToThreadHandler = useHandler((threadId: string) => {
    const thread = threads.find((t: SupportThread) => t._id === threadId);
    if (thread) {
      void loadThreadIntoChat(threadId, thread);
    }
  });

  useEffect(() => {
    setNavigateToThread(navigateToThreadHandler);
    return () => setNavigateToThread(null);
  }, [navigateToThreadHandler, setNavigateToThread]);

  const {
    isLoadingThread,
    setIsLoadingThread,
    notifyClosedEscalationMessage,
    setDismissedEscalationId,
  } = useEscalationNotifications({
    isOpen,
    open,
    threads,
    escalationContext,
    merchantRpcClient,
    loadThreadIntoChat,
    userEmail: user.email,
    hasInitializedRef,
    pendingThreadLoadInFlightRef,
  });

  useCodeSearchNotification({
    escalationContext,
    merchantRpcClient,
    isOpen,
    open,
  });

  const { agentName, setAgentName, agentAvatarUrl, setAgentAvatarUrl } =
    useHumanMessageHandler({
      addMessage,
      processedMessageIdsRef,
      authorRegistryRef,
      isOpenRef,
      onClosedPanelMessage: notifyClosedEscalationMessage,
    });

  const startNewConversation = useHandler(() => {
    onChatOpen?.();
    stopStream();
    clearAssets();
    clearMessages();
    setInputValue("");
    setCurrentThreadId(undefined);
    clearEscalation();
    clearEscalationNotification();
    setDismissedEscalationId(undefined);
    setAgentName(null);
    setAgentAvatarUrl(null);
    processedMessageIdsRef.current.clear();
    authorRegistryRef.current.clear();
    hasInitializedRef.current = true;
  });

  // When the panel opens, start a new conversation unless it's a direct escalation
  useEffect(() => {
    if (isOpen && !hasInitializedRef.current) {
      if (!panelOptions?.directEscalation) {
        startNewConversation();
      }
      hasInitializedRef.current = true;
    }

    // Reset initialization flag when panel closes
    if (!isOpen) {
      hasInitializedRef.current = false;
    }
  }, [isOpen, startNewConversation, panelOptions]);

  // Handle prefill input when panel opens with options
  useEffect(() => {
    if (panelOptions?.prefillInput) {
      setInputValue(panelOptions.prefillInput);
      if (panelOptions.autoSubmit) {
        const prefillValue = panelOptions.prefillInput;
        const timeoutId = setTimeout(() => {
          sendMessage(prefillValue);
        }, 100);
        return () => clearTimeout(timeoutId);
      }
      clearPanelOptions();
    }
    return undefined;
  }, [panelOptions, setInputValue, sendMessage, clearPanelOptions]);

  // Memoize the escalation status element to keep a stable reference during resize
  const escalationStatusElement = useMemo(
    () => (
      <>
        <EscalationStatus
          agentAvatarUrl={agentAvatarUrl}
          agentName={agentName}
        />
        {escalationContext.escalation.status === "closed" &&
          escalationContext.escalation.escalationId && (
            <EscalationCsatForm
              escalationId={escalationContext.escalation.escalationId}
            />
          )}
      </>
    ),
    [
      agentAvatarUrl,
      agentName,
      escalationContext.escalation.status,
      escalationContext.escalation.escalationId,
    ],
  );

  return (
    <MessageAuthorContext.Provider value={messageAuthorContextValue}>
      <AiSlideOutChatGlobal
        aboveInput={escalationStatusElement}
        currentThreadId={currentThreadId}
        disableChatInput={escalationContext.escalation.status === "waiting"}
        hideClearButton
        isLoadingThread={isLoadingThread}
        onClose={close || (() => {})}
        onLoadThread={loadThreadIntoChat}
        onNewConversation={startNewConversation}
        suggestedQuestions={suggestedQuestions}
        threads={threads}
      />
    </MessageAuthorContext.Provider>
  );
});
